import { trackEvent } from "@kunduz/utility-analytics";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { ReactElement, useEffect } from "react";
import { QIBreadcrumb } from "../../components/breadcrumb";
import styles from "./coursePage.module.scss";
import Sticky from "../../../../common/components/sticky";
import { QuestionItem } from "@kunduz/feature-question-indexing";
import { QuestionIndexing } from "@kunduz/data-access-qi-api";
import QuestionSideCtaLoader, {
  SideCTAModes,
} from "../../../../common/components/sideCta";
import Pagination from "../../components/pagination/pagination";
import Link from "next/link";
import { getQIUrl } from "@kunduz/utility-common";
import { QIFilterMenu } from "../../components/qiFilterMenu/qiFilterMenu";
import {
  WebsiteEnFooter,
  DynamicHeader,
  HeaderBannerWithContext,
} from "@kunduz/feature-landing-pages";
import { type IContent } from "@kunduz/data-access-cms-api";

const CourseSUGPage = (props: Props): ReactElement => {
  const { t } = useTranslation("questionIndexing");

  useEffect(() => {
    if (!props.course) return;
    trackEvent("Question Indexing Subject List Page Visited", {
      course: props.course.name,
    });
  }, [props.course]);

  return (
    <>
      <NextSeo
        title={`${t("course_detail_meta_title", {
          course: props.course?.name,
        })}`}
        description={t("course_detail_meta_description", {
          course: props.course?.name,
        })}
        canonical={getQIUrl({
          lang: props.lang,
          domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
          courseSlug: props.course.slug,
        })}
        robotsProps={{
          maxImagePreview: "large",
        }}
      />

      {props.headerBanner && (
        <HeaderBannerWithContext
          label={props.headerBanner.content}
          buttonLabel={props.headerBanner.cta_title}
          url={props.headerBanner.cta_link}
        />
      )}

      <DynamicHeader
        lang={props.lang}
        headerType="student"
        headerItems={props.headerItems}
      />

      <div className={styles.pageContainer}>
        <div className={styles.breadcrumb}>
          <QIBreadcrumb lang={props.lang} course={props.course} linkAs={Link} />
        </div>
        <aside className={styles.filterMenuContainer}>
          <QIFilterMenu
            courses={props.courses}
            subjects={props.course.subjects}
            activeCourseSlug={props.course.slug}
            lang={props.lang}
            subjectAndCoursesTitle={t("question_indexing_leftbar_header")}
            linkAs={Link}
          />
        </aside>
        <main className={styles.content}>
          <div className={styles.courseContainer}>
            <div className={styles.headersContainer}>
              <h1>
                {t("question_indexing_course_detail_page_title", {
                  term: props.course.name,
                })}
              </h1>
              {!props.isMobile && (
                <div className={styles.shortDesc}>
                  {t("question_indexing_course_list_page_introduction_text")}
                </div>
              )}

              {props.questions && (
                <>
                  <section id="questions" className={styles.questions}>
                    {props.questions.map((question, index) => (
                      <QuestionItem
                        key={index}
                        description={question.text_content}
                        imageUrl={question.image}
                        imgAlt={question.text_content}
                        subjectName={question.subject}
                        courseName={question.course}
                        url={getQIUrl({
                          lang: props.lang,
                          questionSlug: question.slug,
                          questionId: question.id,
                        })}
                        linkAs={Link}
                      />
                    ))}
                  </section>

                  <Pagination
                    base={getQIUrl({
                      lang: props.lang,
                      courseSlug: props.course.slug,
                    })}
                    currentPage={props.page}
                    perPage={props.perPage}
                    total={props.total}
                    linkAs={Link}
                  />
                </>
              )}
            </div>
          </div>
        </main>
        <aside className={styles.sticky}>
          <Sticky top={100}>
            <QuestionSideCtaLoader
              isMobile={props.isMobile}
              lang={props.lang}
              mode={SideCTAModes.QI}
              translationFile="questionIndexing"
            />
          </Sticky>
        </aside>
      </div>

      <WebsiteEnFooter />
    </>
  );
};

interface Props {
  course: QuestionIndexing.IQiCourse;
  courses: QuestionIndexing.IQiCourse[];
  questions: QuestionIndexing.UsQiQuestion[];
  total: number;
  page: number;
  perPage: number;
  isAuth: boolean;
  isMobile: boolean;
  lang: "tr" | "en";
  headerItems: IContent.HeaderItemsResponse;
  headerBanner?: IContent.HeaderBannerResponse;
}

export default CourseSUGPage;
